import { UserError } from "@/framework/errors/UserError";
import { LoadableValue } from "@/lib/mobx/LoadableValue";
import { SettingsState } from "@/modules/settings/SettingsState";
import {
  MAX_TRANSLATION_BATCH_SIZE,
  TextTranslationItem,
  TrackParagraphTranslationItem,
  TranslationBatchItem,
  TranslationBatchItemResult,
} from "@/modules/translation/TranslationBatchItem";
import { unpackTrpcErrors } from "@/server/client/unpackTrpcError";
import { trpc } from "@/utils/trpc";
import { BatchItemResult, BatchScheduler } from "./BatchScheduler";

// First request waits 50ms for siblings (e.g. paragraphs scrolling into view),
// never more than 200ms.
const TRANSLATION_BATCH_OPTIONS = {
  debounceMilliseconds: 50,
  maxDelayMilliseconds: 200,
  maxBatchSize: Math.min(MAX_TRANSLATION_BATCH_SIZE, 10),
};

// Single entry point for all of a workspace's translation requests.
// Groups requests made in quick succession into one translateBatch call.
export class TranslationRequestQueue {
  private scheduler = new BatchScheduler<TranslationBatchItem, string | null>(
    (items) => this.sendBatch(items),
    TRANSLATION_BATCH_OPTIONS,
  );

  constructor(private settingsState: LoadableValue<SettingsState>) {}

  // Resolves to null when `onlyIfExists` is set and nothing is stored yet.
  translateTrackParagraph(
    item: Omit<TrackParagraphTranslationItem, "kind">,
  ): Promise<string | null> {
    return this.scheduler.request({ kind: "trackParagraph", ...item });
  }

  async translateText(item: Omit<TextTranslationItem, "kind">) {
    const translation = await this.scheduler.request({ kind: "text", ...item });
    if (translation === null) {
      throw new UserError({ message: "translation is empty" });
    }
    return translation;
  }

  private async sendBatch(
    items: TranslationBatchItem[],
  ): Promise<BatchItemResult<string | null>[]> {
    const settingsState = await this.settingsState.load();
    const { results } = await unpackTrpcErrors(() =>
      trpc.translations.translateBatch.mutate({
        items,
        apiKeys: {
          deeplApiKey: settingsState.deeplApiKeyState.value,
          googleTranslateApiKey: settingsState.googleTranslateApiKeyState.value,
        },
      }),
    );
    return results.map(toBatchItemResult);
  }
}

function toBatchItemResult(
  result: TranslationBatchItemResult,
): BatchItemResult<string | null> {
  if ("error" in result) {
    return { ok: false, error: new UserError(result.error) };
  }
  return { ok: true, value: result.translation };
}
